import { FilterGroupOption } from "@components/common/data-grid/adm-data-grid-types";
import { blue, Text } from "@flat-ui";
// eslint-disable-next-line no-restricted-imports -- A multi-select needs MUI's MenuItem, which flat-ui does not wrap
import { MenuItem } from "@mui/material";

/**
 * Values the bulk actions report themselves with. The select identifies the clicked item by its
 * value, so the actions need one, but it is swapped for the resulting selection before it is
 * passed on and never reaches the filter model.
 */
export const SELECT_ALL_VALUE = "__select-all__";
export const DESELECT_ALL_VALUE = "__deselect-all__";

/** Half a row each, so that both bulk actions share a single band above the options */
const BULK_ACTION_SX = {
  display: "inline-flex",
  width: "50%",
  verticalAlign: "top",
  px: 1.5,
  borderBottom: 1,
  borderColor: "divider",
};

interface Props {
  /** The options offered inside the dropdown */
  options: FilterGroupOption[];

  /** Ids of the options currently selected */
  selectedIds: string[];
}

/**
 * Builds the bulk actions as plain elements rather than a component, because the select wires the
 * selection up by cloning its direct children. Both stay in place and are only disabled, so that
 * activating one neither drops the keyboard focus nor slides the other one under the pointer that
 * just clicked.
 *
 * @param props the options of the dropdown and the selection they are acted on against
 * @returns the actions, each an item of its own, as that is all the select lets the keyboard reach
 */
export function renderBulkActions({
  options,
  selectedIds,
}: Props): React.JSX.Element[] {
  const actions = [
    {
      value: SELECT_ALL_VALUE,
      text: "Select All",
      align: "flex-start",
      isDisabled: options.every(({ id }) => selectedIds.includes(id)),
    },
    {
      value: DESELECT_ALL_VALUE,
      text: "Deselect All",
      align: "flex-end",
      isDisabled: selectedIds.length === 0,
    },
  ];

  return actions.map(({ value, text, align, isDisabled }) => (
    <MenuItem
      key={value}
      value={value}
      disabled={isDisabled}
      sx={{ ...BULK_ACTION_SX, justifyContent: align }}
    >
      {/* Carries the color of a ghost button, since the item acts like one */}
      <Text variant="bodyM" sx={{ color: blue[500] }}>
        {text}
      </Text>
    </MenuItem>
  ));
}
